import React from 'react';
import PropTypes from 'prop-types';

import {
  ELEVATED_PROMO_PRICE_ROW_LAYOUT,
  LIMITED_TIME_OFFER_BANNER_VARIANT,
} from '../../../constants/campaignPromoVariants';
import { SUBSCRIPTION_TYPES } from '../../../constants/subscriptions';
import LimitedTimeOfferBanner from '../../CampaignPromo/LimitedTimeOfferBanner';
import {
  CampaignPriceAlignmentSpacer,
  ElevatedPromoPriceRow,
} from '../../CampaignPromo/PromoPriceDisplay';
import {
  COMPARISON_TABLE_CAMPAIGN_PRICE_CELL_CLASS,
  COMPARISON_TABLE_FEATURE_ROW_CLASS,
  COMPARISON_TABLE_LEGACY_FEATURE_NAME_ROW_CLASS,
  COMPARISON_TABLE_LEGACY_PLAN_ROW_CLASS,
  COMPARISON_TABLE_WIDTH_CLASS,
} from '../constants';
import {
  ComparisonFeatureNameRows,
  ComparisonPlanFeatureRows,
} from './comparisonTableShared';

const LegacyComparisonTable = ({ features, className }) => (
  <div className={`flex flex-col rounded-lg md:flex-row ${className}`}>
    <div className="flex w-full flex-col items-start rounded-l-lg border border-gray-20 bg-white md:w-55">
      <div className="flex shrink-0 items-center justify-center gap-2.5 self-stretch bg-gray-20 px-6 py-3 text-sm font-semibold leading-5 text-gray-90">
        FEATURES
      </div>
      <div className="flex flex-1 flex-col gap-1 self-stretch px-6 pb-5">
        <div className="flex min-h-[92px] w-42 flex-1 flex-col items-start justify-center self-stretch py-5 text-sm font-semibold leading-5 text-deep-blue-70">
          Price
        </div>
        <ComparisonFeatureNameRows
          features={features}
          rowClassName={COMPARISON_TABLE_LEGACY_FEATURE_NAME_ROW_CLASS}
        />
      </div>
    </div>

    <div className="flex w-55 flex-col items-start border border-gray-20 bg-white">
      <div className="flex shrink-0 items-center justify-center gap-2.5 self-stretch bg-gray-20 px-6 py-3 text-sm font-semibold leading-5 text-gray-90">
        STANDARD
      </div>
      <div className="flex flex-1 flex-col gap-1 self-stretch px-6 pb-5">
        <div className="flex min-h-[92px] w-full flex-col items-center justify-center">
          <h3 className="flex items-center justify-center gap-1 self-stretch text-xl font-semibold leading-8 text-gray-90">
            $0
          </h3>
          <span className="text-center text-sm font-normal leading-5 text-gray-90">
            Your current profile
          </span>
        </div>
        <ComparisonPlanFeatureRows
          features={features}
          plan="standard"
          rowClassName={COMPARISON_TABLE_LEGACY_PLAN_ROW_CLASS}
        />
      </div>
    </div>

    <div className="flex h-[548px] w-78 flex-col items-center rounded-r-lg border border-blue-30 bg-white shadow-elevated">
      <div className="flex w-full shrink-0 items-center justify-center gap-2.5 bg-linear-to-r from-blue-50 to-blue-30 px-2 py-3 text-sm font-semibold leading-5 text-white">
        ELEVATED
      </div>
      <div className="flex flex-1 flex-col gap-1 self-stretch px-6 pb-5">
        <div className="flex items-center gap-5">
          <div className="flex w-33 flex-col items-center py-5">
            <div className="flex items-center gap-1">
              <h3 className="mb-0 text-xl font-semibold leading-8 text-gray-90">
                {SUBSCRIPTION_TYPES.MONTHLY.displayPrice}
              </h3>
              <span className="whitespace-nowrap text-sm font-normal leading-5 text-gray-90">
                &nbsp;/ month
              </span>
            </div>
            <span className="text-sm font-normal leading-5 text-gray-90">
              billed monthly
            </span>
          </div>
          <div className="h-15 w-px bg-gray-20" />
          <div className="flex flex-col items-center py-5">
            <div className="flex items-center gap-1">
              <h3 className="mb-0 text-xl font-semibold leading-8 text-gray-90">
                {SUBSCRIPTION_TYPES.ANNUAL.displayPrice}
              </h3>
              <span className="whitespace-nowrap text-sm font-normal leading-5 text-gray-90">
                &nbsp;/ year
              </span>
            </div>
            <span className="text-sm font-normal leading-5 text-gray-90">
              billed annually
            </span>
          </div>
        </div>
        <ComparisonPlanFeatureRows
          features={features}
          plan="elevated"
          rowClassName={COMPARISON_TABLE_LEGACY_PLAN_ROW_CLASS}
          checkIconClassName="text-blue-30"
        />
      </div>
    </div>
  </div>
);

LegacyComparisonTable.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      standard: PropTypes.bool.isRequired,
      elevated: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  className: PropTypes.string,
};

/**
 * ComparisonTable component displays a side-by-side comparison of features
 * available in standard vs elevated profiles
 */
const ComparisonTable = ({
  features = [],
  showCampaignPricing = false,
  className = '',
}) => {
  if (!showCampaignPricing) {
    return <LegacyComparisonTable features={features} className={className} />;
  }

  const campaignFeatureNameRowClass = `${COMPARISON_TABLE_FEATURE_ROW_CLASS} items-start text-sm font-semibold leading-5 text-deep-blue-70`;
  const campaignPlanRowClass = `${COMPARISON_TABLE_FEATURE_ROW_CLASS} items-center self-stretch`;

  return (
    <div
      className={`relative ${COMPARISON_TABLE_WIDTH_CLASS} pt-10 ${className}`}
    >
      <div className="flex flex-col items-stretch md:flex-row">
        {/* Features Column */}
        <div className="flex w-full shrink-0 flex-col rounded-l-lg border border-gray-20 bg-white md:w-55">
          <div className="flex shrink-0 items-center justify-center gap-2.5 self-stretch bg-gray-20 px-6 py-3 text-sm font-semibold leading-5 text-gray-90">
            FEATURES
          </div>
          <div className="flex flex-1 flex-col gap-1 self-stretch px-6 pb-5">
            <div
              className={`flex w-42 flex-1 flex-col items-start justify-center self-stretch py-5 text-sm font-semibold leading-5 text-deep-blue-70 ${COMPARISON_TABLE_CAMPAIGN_PRICE_CELL_CLASS}`}
            >
              Price
            </div>
            <ComparisonFeatureNameRows
              features={features}
              rowClassName={campaignFeatureNameRowClass}
            />
          </div>
        </div>

        {/* Standard Column */}
        <div className="flex w-55 shrink-0 flex-col border border-gray-20 bg-white">
          <div className="flex shrink-0 items-center justify-center gap-2.5 self-stretch bg-gray-20 px-6 py-3 text-sm font-semibold leading-5 text-gray-90">
            STANDARD
          </div>
          <div className="flex flex-1 flex-col gap-1 self-stretch px-6 pb-5">
            <div
              className={`flex w-full flex-col items-center justify-center ${COMPARISON_TABLE_CAMPAIGN_PRICE_CELL_CLASS}`}
            >
              <CampaignPriceAlignmentSpacer />
              <h3 className="flex items-center justify-center gap-1 self-stretch text-xl font-semibold leading-8 text-gray-90">
                $0
              </h3>
              <span className="text-center text-sm font-normal leading-5 text-gray-90">
                Your current profile
              </span>
            </div>
            <ComparisonPlanFeatureRows
              features={features}
              plan="standard"
              rowClassName={campaignPlanRowClass}
            />
          </div>
        </div>

        {/* Elevated Column */}
        <div className="flex w-78 shrink-0 flex-col self-stretch">
          <div className="flex min-h-full flex-1 flex-col overflow-visible rounded-br-lg border border-blue-30 bg-white shadow-elevated">
            <div className="relative overflow-visible">
              <div
                className="absolute left-1/2 top-[-52px] z-20 -translate-x-1/2"
                data-qa-target="limited-time-offer-badge-anchor"
              >
                <LimitedTimeOfferBanner
                  variant={LIMITED_TIME_OFFER_BANNER_VARIANT.COMPACT}
                />
              </div>
              <div className="relative z-0 flex w-full shrink-0 items-center justify-center gap-2.5 bg-linear-to-r from-blue-50 to-blue-30 px-2 py-3 text-sm font-semibold leading-5 text-white">
                ELEVATED
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-1 self-stretch overflow-hidden px-6 pb-5">
              <div className="flex min-h-[114px] shrink-0 items-center justify-center gap-3">
                <ElevatedPromoPriceRow
                  layout={ELEVATED_PROMO_PRICE_ROW_LAYOUT.SPLIT}
                />
              </div>
              <ComparisonPlanFeatureRows
                features={features}
                plan="elevated"
                rowClassName={campaignPlanRowClass}
                checkIconClassName="text-blue-30"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ComparisonTable.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      standard: PropTypes.bool.isRequired,
      elevated: PropTypes.bool.isRequired,
    }),
  ),
  showCampaignPricing: PropTypes.bool,
  className: PropTypes.string,
};

export default ComparisonTable;
